import { CheckCircle2, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

import { GuideItemConfig, GuideItemType } from '../constants/guide-items';
import { useCompleteGuideItem } from '../hooks/use-complete-guide-item';
import { getItemCompleted } from '../utils/get-item-completed';

interface GuideItemProps {
  config: GuideItemConfig;
  index: number;
  completionMap: Map<string, boolean>;
  onClosePopover: () => void;
}

export function GuideItem({ config, index, completionMap, onClosePopover }: GuideItemProps) {
  const { t } = useTranslation('authenticated.onboarding-checklist');
  const navigate = useNavigate();
  const completeItem = useCompleteGuideItem();

  const isCompleted = getItemCompleted(config, completionMap);

  const handleSettingsAction = () => {
    if (config.completableOnClick) {
      completeItem.mutate(config.key);
    }
    onClosePopover();
    if (config.route) {
      navigate(config.route);
    }
  };

  const handleDocAction = () => {
    completeItem.mutate(config.key);
    if (config.docUrl) {
      window.open(config.docUrl, '_blank');
    }
  };

  if (isCompleted) {
    return (
      <div className="flex w-full items-center gap-3 rounded-md px-2 py-2.5">
        <div className="flex size-6 shrink-0 items-center justify-center">
          <CheckCircle2 className="size-5 text-green-600" />
        </div>
        <span className="flex-1 text-sm text-muted-foreground line-through">
          {t(config.titleKey)}
        </span>
      </div>
    );
  }

  return (
    <Collapsible>
      <CollapsibleTrigger className="flex w-full items-center gap-3 rounded-md px-2 py-2.5 text-left hover:bg-accent">
        <div className="flex size-6 shrink-0 items-center justify-center">
          <span className="flex size-5 items-center justify-center rounded-full border text-xs font-medium">
            {index + 1}
          </span>
        </div>
        <span className="flex-1 text-sm">{t(config.titleKey)}</span>
        <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pb-2 pl-11 pr-2 pt-1">
          <p className="mb-3 text-sm text-muted-foreground">{t(config.descriptionKey)}</p>

          {config.type === GuideItemType.COMPOSITE && config.subItems ? (
            <div className="flex flex-col gap-2">
              {config.subItems.map(subItem => {
                const subCompleted = completionMap.get(subItem.key) ?? false;
                return (
                  <Button
                    key={subItem.key}
                    variant="outline"
                    size="sm"
                    disabled={subCompleted}
                    onClick={() => {
                      completeItem.mutate(subItem.key);
                      onClosePopover();
                      if (subItem.route) {
                        navigate(subItem.route);
                      }
                    }}
                    className="justify-start gap-2"
                  >
                    {subCompleted && <CheckCircle2 className="size-4 text-green-600" />}
                    {t(subItem.labelKey)}
                  </Button>
                );
              })}
            </div>
          ) : config.type === GuideItemType.DOC ? (
            <Button variant="outline" size="sm" onClick={handleDocAction}>
              {t('actions.readArticle')}
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={handleSettingsAction}>
              {t('actions.goToSettings')}
            </Button>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
